import { ADVISOR_ROUTING_PREFIX } from '@/router/constants'
import { featureFlag, featureValue } from '@/../app-config.js'

const getRouteNames = route => {
  const childRouteNames = []
  if (route.children) {
    route.children.forEach(route =>
      childRouteNames.push(...getRouteNames(route))
    )
  }
  return [route.name, ...childRouteNames]
}

const enrichRoutesWithMetaPos = (routes) => {
  let count = 0
  for (const route of routes) {
    if (route?.meta?.pos !== 'ignoreStep') {
      const meta = route.meta || {}
      route.meta = { ...meta, pos: count + 1 }
      
      count += 1
    }
  }

  return routes
}

const allocationContractRoute = (pages, isReallocation = false) => {
  const suffix = isReallocation ? '-reallocation' : ''

  const children = enrichRoutesWithMetaPos([
    ...(featureFlag('aEnableCustomerVerification')
      ? [
          {
            path: 'verification',
            name: `customer-verification-allocation${suffix}`,
            component: pages.customerVerificationAllocation
          }
        ]
      : []),
    {
      path: 'current',
      name: `current-allocation${suffix}`,
      component: pages.customerContractAllocation
    },
    ...(featureFlag('aEnableAllocationFundDetails')
      ? [
          {
            path: 'current/step/:step/fund/:isin',
            name: `customer-allocation-fund${suffix}`,
            component: pages.customerAllocationFund,
            redirect: { name: `fund-allocation-overview${suffix}` },
            meta: { pos: 'ignoreStep' },
            props: true,
            children: [
              {
                path: 'overview',
                name: `fund-allocation-overview${suffix}`,
                component: pages.fundOverview
              }
            ]
          }
        ]
      : []),
    {
      path: 'recommendation',
      name: `allocation-recommendation${suffix}`,
      component: pages.customerAllocationRecomendation
    },
    {
      path: 'funds-list-modification',
      name: `allocation-recomendation-funds-list-modification${suffix}`,
      component: pages.customerAllocationRecomendationFundsListModification,
      meta: { isStepperVisible: false, pos: 'ignoreStep' }
    },
    {
      path: 'summary',
      name: `allocation-recommendation-summary${suffix}`,
      component: pages.customerAllocationRecomendationSummary
    },
    ...(featureFlag('aEnableAllocationFeesStep')
      ? [
          {
            path: 'fees',
            name: `allocation-fees${suffix}`,
            component: pages.customerAllocationFees
          }
        ]
      : []),
    ...(!featureFlag('disablePerformances')
      ? [
          {
            path: 'arbitration-performance-calculation',
            name: `arbitration-performance-calculation${suffix}`,
            component: pages.advisoryArbitrationPerformanceCalculation
          }
        ]
      : [])
  ])

  const route = {
    path: 'allocation/contract/:contractId',
    name: `customer-allocation-steps${suffix}`,
    component: pages.customerAllocationSteps,
    redirect: {
      name: featureFlag('aEnableCustomerVerification') ? `customer-verification-allocation${suffix}` : `current-allocation${suffix}`
    },
    meta: { view: true },
    children
  }

  if (isReallocation) {
    addIsParentStepperVisible(route)
  } else {
    route.children.push({
      path: 'arbitration-pdf-preview',
      name: `arbitration-pdf-preview${suffix}`,
      component: pages.advisoryArbitrationPdfPreview,
      meta: { pos: route.children[route.children.length - 1]?.meta?.pos + 1 }
    })
  }

  return route
}

const addIsParentStepperVisible = routes => {
  for (const route of routes.children) {
    if (route.meta) {
      route.meta.isParentStepperVisible = false
    } else {
      route.meta = { isParentStepperVisible: false }
    }

    if (route.children) {
      addIsParentStepperVisible(route)
    }
  }
}

export const AdvisoryRedirect = authenticatedRoutes => {
  const array = []

  authenticatedRoutes.forEach(route => array.push(...getRouteNames(route)))
  return array
}

export const wealthDecisionRoutes = pages => {
  if (!featureFlag('wealthDecisionVisible')) return []

  return [
    {
      path: 'estate',
      name: 'customer-estate-steps',
      component: pages.customerEstateSteps,
      redirect: {
        name: 'definition-available-savings'
      },
      meta: { view: true },
      children: [
        {
          path: 'definition-available-savings',
          name: 'definition-available-savings',
          component: pages.customerDefinitionAvailableSavings,
          meta: { pos: 1 }
        },
        {
          path: 'inter-envelope',
          name: 'inter-envelope-reallocation',
          component: pages.customerInterEnvelopeReallocation,
          meta: { pos: 2 }
        },
        {
          path: 'fees',
          name: 'estate-fees',
          component: pages.customerEstateFees,
          meta: { pos: 3 }
        },
        {
          path: 'intra-envelope',
          name: 'intra-envelope-reallocation',
          component: pages.customerIntraEnvelopeReallocation,
          meta: { pos: 4 }
        },
        {
          path: 'arbitrations',
          name: 'arbitration-list-reallocation',
          component: pages.customerArbitrationListReallocation,
          meta: { pos: 5 }
        },
        {
          path: 'validation-proposition',
          name: 'validation-proposition',
          component: pages.customerEstateReportPreview,
          meta: { pos: 6 }
        },
        {
          path: 'contract/:contractId/funds-list-modification',
          name: 'estate-funds-list-modification',
          component: pages.customerAllocationRecomendationFundsListModification,
          meta: { isStepperVisible: false }
        },
        allocationContractRoute(pages, true)
      ]
    }
  ]
}

export const multiArbitrationRouteNamesConfig = [
  'funds-to-disinvest',
  'funds-to-invest',
  'contract-arbitration',
  'compliance-customer',
  'data-verification',
  'fees'
]

export const multiArbitrationConvergenceRouteNamesConfig = [
  'convergence-select-model-portfolio',
  'convergence-contract-arbitration',
  'convergence-compliance-customer',
  'convergence-data-verification',
  'convergence-fees'
]

export const arbitrationPrefixRoutes = {
  multiArbitration: 'multi-arbitration',
  convergenceModelPortfolio: 'convergence-model-portfolio'
}

const enrichRoutes = (routes, routeNames) => {
  return routes.map((step, index) => {

    const children = []
    if (step.children) {
      for (const line of step.children) {
        children.push({ ...line, meta: { ...line.meta, pos: index + 1 } })
      }
    }

    return {
      ...step,
      children,
      path: routeNames[index],
      name: routeNames[index],
      meta: { pos: index + 1 }
    }
  })
}

export const multiArbitrationStepsRoutes = (routes, pages, routeNames, mainPage, mainRouteName = arbitrationPrefixRoutes.multiArbitration) => {
  const steps = enrichRoutes(routes, routeNames)

  return [
    {
      path: `product-customer-arbitrations/${mainRouteName}/:bankContractId`,
      name: mainRouteName,
      component: mainPage,
      redirect: {
        name: `${mainRouteName}-step`
      },
      children: [
        {
          path: 'steps',
          name: `${mainRouteName}-step`,
          component: pages.multiArbitrationStep,
          redirect: {
            name: steps[0].name
          },
          children: [
            ...steps,
            {
              path: 'current/step/:step/fund/:isin',
              name: `${mainRouteName}-multi-arbitration-allocation-fund`,
              component: pages.customerAllocationFund,
              redirect: { name: `${mainRouteName}-multi-arbitration-allocation-fund-overview`, },
              meta: { isStepperVisible: false },
              props: true,
              children: [
                {
                  path: 'overview',
                  name: `${mainRouteName}-multi-arbitration-allocation-fund-overview`,
                  component: pages.fundOverview
                }
              ]
            },
            {
              path: 'funds-list-modification',
              name: 'multi-arbitration-funds-list-modification',
              component: pages.customerAllocationRecomendationFundsListModification,
              meta: { isStepperVisible: false },
              props: true
            }
          ]
        }
      ]
    }
  ]
}

export const switchAdvisoryName = route => {
  return {
    ...route,
    name: route.name && `${ADVISOR_ROUTING_PREFIX}${route.name}`,
    redirect: route.redirect
      ? {
          name: `${ADVISOR_ROUTING_PREFIX}${route.redirect.name}`
        }
      : undefined,
    children: route.children
      ? route.children.map(route => switchAdvisoryName(route))
      : undefined
  }
}

export default (pages, authenticatedRoutes) => {
  const advisoryCustomerAuthenticatedRoutes = authenticatedRoutes.map(route =>
    switchAdvisoryName(route)
  )

  const multiArbitrationContract = (prefix) => {
    return {
      component: pages.multiArbitrationContractStep,
      redirect: { name: `${prefix}-contract-orders` },
      children: [
        {
          name: `${prefix}-contract-orders`,
          path: `${prefix}-contract-orders`,
          component: pages.contractOrders
        },
        {
          name: `${prefix}-contract-justifications`,
          path: `${prefix}-contract-justifications`,
          component: pages.contractJustifications
        }
      ]
    }
  }

  const multiArbitrationCompliance = (prefix) => {
    return {
      component: pages.multiArbitrationComplianceStep,
      redirect: { name: `${prefix}-perimeter-arbitrated` },
      children: [
        {
          name: `${prefix}-perimeter-arbitrated`,
          path: `${prefix}-perimeter-arbitrated`,
          component: pages.multiArbitrationPerimeterArbitrated
        },
        {
          name: `${prefix}-out-perimeter`,
          path: `${prefix}-out-perimeter`,
          component: pages.multiArbitrationOutPerimeter
        }
      ]
    }
  }

  const routesMultiArbitrationSaleOneFund = [
    { component: pages.multiArbitrationDisinvestStep },
    { component: pages.multiArbitrationInvestStep },
    multiArbitrationContract('one-fund'),
    multiArbitrationCompliance('one-fund'),
    { component: pages.multiArbitrationVerificationStep },
    { component: pages.multiArbitrationFeesStep }
  ]



  const multiArbitrationModelPortfolio = (name) => {
    return {
      component: pages.multiArbitrationModelPortfolio,
      redirect: { name: `${name}-investment` },
      children: [
        {
          name: `${name}-investment`,
          path: `investment`,
          component: pages.multiArbitrationModelPortfolioInvestment,
          meta: { takeParentStepper: true }
        },
        {
          name: `${name}-repartition`,
          path: `repartition`,
          component: pages.multiArbitrationModelPortfolioRepartition,
          meta: { takeParentStepper: true }
        },
        {
          name: `${name}-backtest`,
          path: `backtest`,
          component: pages.multiArbitrationModelPortfolioBacktest,
          meta: { takeParentStepper: true }
        },
        {
          name: `${name}-esg`,
          path: `esg`,
          component: pages.multiArbitrationModelPortfolioESG,
          meta: { takeParentStepper: true }
        }
      ]
    }
  }

  const routesMultiArbitrationConvergence = [
    multiArbitrationModelPortfolio(multiArbitrationConvergenceRouteNamesConfig[0]),
    { component: pages.multiArbitrationDisinvestStep },
    multiArbitrationCompliance('convergence'),
    { component: pages.multiArbitrationVerificationStep },
    { component: pages.multiArbitrationFeesStep }
  ]

  return {
    path: '/advisory',
    name: 'advisory-authenticated',
    meta: {
      advisory: true,
      authenticated: true
    },
    component: pages.advisoryPage,
    redirect: {
      name: 'advisory-customers'
    },
    children: [
      {
        path: 'hidden-page',
        name: 'hidden-page',
        component: pages.hiddenPage
      },
      {
        path: 'customers',
        name: 'advisory-customers',
        component: pages.advisoryCustomers,
        redirect: {
          name: 'customers-list'
        },
        children: [
          {
            path: 'list',
            name: 'customers-list',
            component: pages.advisoryCustomersList
          },
          {
            path: 'add',
            name: 'customers-add',
            component: pages.advisoryCustomersAdd
          },
          {
            path: ':customerId',
            name: 'customer-info',
            component: pages.advisoryCustomerInfos,
            redirect: {
              name: 'customer-overview'
            },
            props: true,
            children: [
              {
                path: 'overview',
                name: 'customer-overview',
                component: pages.customerOverview
              },
              ...(featureFlag('aEnableSimpleArbitration')
                ? [
                    {
                      path: 'arbitrations',
                      name: 'customer-arbitrations',
                      component: pages.customerArbitrations,
                      // This disables the user ratings stuff
                      meta: { view: true }
                    }
                  ]
                : []),
              ...(!featureFlag('disablePerformances')
                ? [
                    {
                      path: 'performances/:contractId/performance-calculation',
                      name: 'performance-calculation',
                      component:
                        pages.advisoryArbitrationPerformanceCalculation,
                      meta: { view: true }
                    }
                  ]
                : []),
              ...(!featureFlag('disablePerformances')
                ? [
                    {
                      path: 'performances',
                      name: 'customer-performances',
                      component: pages.customerPerformances,
                      // This disables the user ratings stuff
                      meta: { view: true }
                    }
                  ]
                : []),
              ...wealthDecisionRoutes(pages),
              allocationContractRoute(pages),
              {
                path: 'stock-exchange-analyse/contract/:contractId',
                name: 'stock-exchange-analyse-steps',
                component: pages.customerStockExchangeSteps,
                redirect: {
                  name: 'stock-exchange-current-allocation'
                },
                props: true,
                meta: { view: true },
                children: [
                  {
                    path: 'current',
                    name: 'stock-exchange-current-allocation',
                    component: pages.customerStockExchangeAllocation,
                    meta: { pos: 1 }
                  },
                  {
                    path: 'analysis',
                    name: 'stock-exchange-analysis',
                    component: pages.customerStockExchangeAnalysis,
                    meta: { pos: 2 }
                  },
                  {
                    path: 'target-portfolio',
                    name: 'stock-exchange-target-portfolio',
                    component: pages.customerStockExchangeTargetPortfolio,
                    meta: { pos: 3 }
                  },
                  ...(!featureFlag('disablePerformances')
                    ? [
                        {
                          path: 'performance-calculation',
                          name: 'stock-exchange-portfolio-performance',
                          component:
                            pages.customerStockExchangePortfolioPerformance,
                          meta: { pos: 4 }
                        }
                      ]
                    : [])
                ]
              },
              {
                path: 'insights',
                name: 'customer-insights'
              },
              {
                path: 'missions',
                name: 'customer-missions',
                component: pages.customerMissions
              },
              {
                path: 'interactions',
                name: 'customer-interactions',
                component: pages.customerInteractions,
                children: [
                  {
                    path: 'inbound-call',
                    name: 'customer-interactions-inbound-call',
                    component: pages.customerInteractions,
                    meta: { type: 'inbound_call' }
                  },
                  {
                    path: 'outbound-call',
                    name: 'customer-interactions-outbound-call',
                    component: pages.customerInteractions,
                    meta: { type: 'outbound_call' }
                  },
                  {
                    path: 'appointment',
                    name: 'customer-interactions-appointment',
                    component: pages.customerInteractions,
                    meta: { type: 'appointment' }
                  },
                  {
                    path: 'next-appointment',
                    name: 'customer-interactions-next-appointment',
                    component: pages.customerInteractions,
                    meta: { type: 'next_appointment' }
                  }
                ]
              },
              ...(featureFlag('aEnableCompliance')
                ? [
                    {
                      path: 'compliance',
                      name: 'customer-compliance',
                      component: pages.customerCompliance,
                      redirect: { name: `compliance-${featureValue('aEnableComplianceTabs')?.[0]}` },
                      children: [
                        ...(featureValue('aEnableComplianceTabs').includes('overview')
                        ? [{
                            path: 'overview',
                            name: 'compliance-overview',
                            component: pages.complianceOverview
                          }]
                        : []),
                        ...(featureValue('aEnableComplianceTabs').includes('risk')
                        ? [{
                            path: 'risk',
                            name: 'compliance-risk',
                            component: pages.complianceRisk
                          }]
                        : []),
                        ...(featureValue('aEnableComplianceTabs').includes('esg')
                        ? [{
                            path: 'esg',
                            name: 'compliance-esg',
                            component: pages.complianceEsg
                          }]
                        : []),
                        ...(featureValue('aEnableComplianceTabs').includes('documents')
                        ? [{
                            path: 'documents',
                            name: 'compliance-documents',
                            component: pages.complianceDocuments
                          }]
                        : [])
                      ]
                    }
                  ]
                : []),
              {
                path: 'club',
                name: 'customer-club',
                component: pages.customerClub
              },
              {
                path: 'view',
                name: 'customer-view',
                component: pages.authenticatedPage,
                meta: {
                  view: true
                },
                props: true,
                redirect: {
                  name: 'advisor-dashboard'
                },
                children: advisoryCustomerAuthenticatedRoutes
              }
            ]
          }
        ]
      },
      ...(featureFlag('aEnableArbitrations')
        ? [
            {
              path: 'arbitrations',
              name: 'advisory-arbitrations',
              component: pages.advisoryArbitrations
            }
          ]
        : []),
      ...(featureFlag('aEnableProductCustomerArbitrations')
        ? [
            {
              path: 'arbitrations',
              name: 'advisory-collective-arbitration-list',
              redirect: {
                name: 'advisory-product-customer-arbitrations'
              },
              component: pages.advisoryCollectiveArbitrationList,
              children: [
                {
                  path: 'product-customer',
                  name: 'advisory-product-customer-arbitrations',
                  component: pages.advisoryProductCustomerArbitrations
                },
                {
                  path: 'history',
                  name: 'advisory-collective-arbitration-history',
                  component: pages.advisoryCollectiveArbitrationHistory
                }
              ]
            },
            ...multiArbitrationStepsRoutes(routesMultiArbitrationSaleOneFund, pages, multiArbitrationRouteNamesConfig, pages.multiArbitrationOnefundPage),
            ...multiArbitrationStepsRoutes(routesMultiArbitrationConvergence, pages, multiArbitrationConvergenceRouteNamesConfig, pages.multiArbitrationConvergencePage , arbitrationPrefixRoutes.convergenceModelPortfolio),
            {
              path: 'product-customer-arbitrations/product/:productId',
              name: 'product-customer-details',
              component: pages.productCustomerDetails,
              redirect: {
                name: 'product-customer-customer'
              },
              children: [
                {
                  path: 'customer',
                  name: 'product-customer-customer',
                  component: pages.productCustomerCustomer
                },
                {
                  path: 'invest',
                  name: 'product-customer-invest',
                  component: pages.productCustomerInvest
                },
                {
                  path: 'repartition',
                  name: 'product-customer-repartition',
                  component: pages.productCustomerRepartition
                },
                {
                  path: 'esg',
                  name: 'product-customer-esg',
                  component: pages.productCustomerEsg
                },
                {
                  path: 'univers',
                  name: 'product-customer-univers',
                  props: true,
                  component: pages.portfolioBuilderProductFondsUniverse
                }
              ]
            }
          ]
        : []),
      {
        path: 'missions',
        name: 'advisory-missions',
        component: pages.advisoryMissionsLayout,
        redirect: {
          name: 'missions-financials'
        },
        children: [
          {
            path: 'financials',
            name: 'missions-financials',
            meta: {
              missionsSubnav: true
            },
            components: {
              default: pages.advisoryMissionsFinancials
            }
          },
          {
            path: 'wizard',
            name: 'missions-wizard',
            meta: {
              fullWidthContent: true
            },
            component: pages.advisoryMissionsWizard
          },
          {
            path: ':id',
            name: 'missions-details',
            meta: {
              fullWidthContent: true
            },
            component: pages.advisoryMissionsDetails
          }
        ]
      },
      {
        path: 'statistics',
        name: 'advisory-statistics',
        component: pages.advisoryStatsLayout,
        meta: {
          isStatisticsAdvisoryAuthorized: true
        },
        redirect: {
          name: 'statistics-global'
        },
        children: [
          {
            path: 'global',
            name: 'statistics-global',
            component: pages.advisoryStatsDashboard,
            meta: {
              isStatisticsAdvisoryAuthorized: true
            }
          },
          {
            path: 'clients-by-advisor',
            name: 'statistics-clients-by-advisor',
            component: pages.advisoryStatsDashboard,
            meta: {
              isStatisticsAdvisoryAuthorized: true
            }
          },
          {
            path: 'historical',
            name: 'statistics-historical',
            component: pages.advisoryStatsDashboard,
            meta: {
              isStatisticsAdvisoryAuthorized: true
            }
          },
          {
            path: 'uncumulated-history',
            name: 'statistics-uncumulated-history',
            component: pages.advisoryStatsDashboard,
            meta: {
              isStatisticsAdvisoryAuthorized: true
            }
          },
          {
            path: 'last-few-days',
            name: 'statistics-last-few-days',
            component: pages.advisoryStatsDashboard,
            meta: {
              isStatisticsAdvisoryAuthorized: true
            }
          }
        ]
      },
      ...(featureFlag('enableEsgSettings')
        ? [
            {
              path: 'advisor-settings',
              name: 'advisor-settings',
              component: pages.advisoryEsgSettings,
              meta: {
                isStatisticsAdvisoryAuthorized: true
              }
            }
          ]
        : []),
      ...(featureFlag('aEnableSupport')
      ? [
          {
            path: 'support',
            name: 'advisory-support',
            component: pages.advisorySupport
          }
        ]
      : []),
      {
        path: 'arbitration-pdf',
        name: 'arbitration-pdf',
        component: pages.advisoryArbitrationPdf
      }
    ]
  }
}
